import { useQuery } from "@apollo/client";
import {
  Box,
  Center,
  Container,
  Grid,
  Loader,
  Stack,
  Text,
} from "@mantine/core";
import { useState } from "react";
import FilterPanel, {
  FilterOption,
  SortOption,
} from "../../components/Filter/FilterPanel";
import PageHeader from "../../components/PageHeader/PageHeader";

interface DiscoverPageProps<T> {
  title: string;
  subtitle: string;
  query: any;
  dataKey: string;
  filterOptions: FilterOption[];
  sortOptions: SortOption[];
  renderCard: (item: T, products?: any) => React.ReactNode;
  initialSortBy: string;
  queryVars?: Record<string, any>;
  filterValidItems?: (item: T) => boolean;
}

export function DiscoverBaseComponent<T>({
  title,
  subtitle,
  query,
  dataKey,
  filterOptions,
  sortOptions,
  renderCard,
  initialSortBy,
  queryVars,
  filterValidItems,
}: DiscoverPageProps<T>) {
  const [items, setItems] = useState<T[]>([]);
  const [filteredItems, setFilteredItems] = useState<T[]>([]);
  const [sortBy, setSortBy] = useState(initialSortBy);
  const [loading, setLoading] = useState(true);

  useQuery(query, {
    variables: queryVars,
    onCompleted(data) {
      const rawData = data[dataKey] as T[];
      // Filter out incomplete profiles if filterValidItems function is provided
      const validData = filterValidItems
        ? rawData.filter(filterValidItems)
        : rawData;
      setItems(validData);
      setFilteredItems(validData);
      setLoading(false);
    },
    skip: !!queryVars && Object.values(queryVars).some((v) => !v),
  });

  function handleClearFilters() {
    // This function should be passed down to FilterPanel
    // and reset all filter states.
  }

  return (
    <Box>
      <PageHeader title={title} subtitle={subtitle} />
      <Container size="xl" py="xl">
        <Stack gap={"xl"}>
          <FilterPanel
            filterOptions={filterOptions}
            sortOptions={sortOptions}
            sortValue={sortBy}
            onSortChange={setSortBy}
            onClearFilters={handleClearFilters}
            expandable={true}
            defaultExpanded={false}
            data={items}
            onFilteredDataChange={setFilteredItems}
          />

          {loading ? (
            <Center h={300}>
              <Loader size="xl" />
            </Center>
          ) : filteredItems.length === 0 ? (
            <Center p="xl">
              <Text size="lg">
                No items found matching your search criteria.
              </Text>
            </Center>
          ) : (
            <Grid>
              {filteredItems.map((item: any) => (
                <Grid.Col
                  key={item.id || item.email}
                  span={{ base: 12, sm: 6, lg: 4 }}
                >
                  {renderCard(item)}
                </Grid.Col>
              ))}
            </Grid>
          )}
        </Stack>
      </Container>
    </Box>
  );
}
